import React, { createContext, useContext, useState, useEffect } from 'react';
import api from '../api/axiosConfig';
import { setBaseTitle } from '../routes/pageTitle';

/**
 * Company Context
 *
 * Provides BC company information throughout the app based on URL path
 * URL format: {tenant}.bcportal.app/{company}/{page}
 * Example: goodstuff365.bcportal.app/crns/dashboard
 *
 * - Tenant comes from subdomain (handled by TenantContext)
 * - Company comes from first path segment (this context)
 * - Company names may contain spaces (URL-encoded as %20)
 */

interface TenantBrandingResponse {
  defaultCompanySlug?: string;
  companyDisplayName?: string | null;
  [key: string]: unknown;
}

interface Company {
  slug: string;
  name: string;
  id: string | null;
  branding: Record<string, string>;
  settings: Record<string, unknown>;
}

interface CompanyContextValue {
  company: Company | null;
  companySlug: string | null;
  loading: boolean;
  error: string | null;
  isCompanyLoaded: boolean;
  buildPath: (path: string) => string;
  navigateTo: (path: string) => void;
  encodeSlug: (slug: string) => string;
}

interface CompanyProviderProps {
  children: React.ReactNode;
}

const CompanyContext = createContext<CompanyContextValue | null>(null);

export const useCompany = (): CompanyContextValue => {
  const context = useContext(CompanyContext);
  if (!context) {
    throw new Error('useCompany must be used within CompanyProvider');
  }
  return context;
};

// Route prefixes that are NOT company slugs
const RESERVED_PREFIXES = ['tntadm', 'glbadm', 'rsladm', 'admin', 'login', 'docs'];

/**
 * Check if the current URL is on a reserved route (admin pages, login).
 */
const isReservedRoute = (): boolean => {
  const firstPart = (window.location.pathname.split('/').filter(p => p.length > 0)[0] || '').toLowerCase();
  return RESERVED_PREFIXES.includes(firstPart);
};

/**
 * Extract company slug from current URL path.
 * Decodes %20 etc. so "CRONUS%20International%20Ltd." → "CRONUS International Ltd."
 * Returns null if no company segment or if on a reserved route (admin pages).
 */
const extractCompanySlug = (): string | null => {
  const path = window.location.pathname;
  const parts = path.split('/').filter(p => p.length > 0);

  if (parts.length > 0) {
    try {
      const slug = decodeURIComponent(parts[0]);
      if (slug.length > 0 && !RESERVED_PREFIXES.includes(slug.toLowerCase())) {
        return slug;
      }
    } catch (_e) {
      const raw = parts[0];
      if (!RESERVED_PREFIXES.includes(raw.toLowerCase())) {
        return raw;
      }
    }
  }

  return null;
};

/**
 * Encode a company name for use in a URL path segment.
 * e.g. "CRONUS International Ltd." → "CRONUS%20International%20Ltd."
 */
const encodeSlug = (slug: string): string => encodeURIComponent(slug);

interface DefaultCompanyResult {
  slug: string;
  displayName: string | null;
}

/**
 * Fetch the default company slug + display name from the branding endpoint.
 * Falls back to localStorage override if the endpoint fails.
 */
const fetchDefaultCompany = async (): Promise<DefaultCompanyResult | null> => {
  const override = localStorage.getItem('dev_company_override');
  if (override) return { slug: override, displayName: null };

  try {
    const resp = await api.get('/tenant/branding') as { data: TenantBrandingResponse };
    if (resp.data?.defaultCompanySlug) {
      return {
        slug: resp.data.defaultCompanySlug,
        displayName: resp.data.companyDisplayName || null,
      };
    }
  } catch (_e) {
    // Not critical — fall back
  }
  return null;
};

export const CompanyProvider: React.FC<CompanyProviderProps> = ({ children }) => {
  const [company, setCompany] = useState<Company | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [companySlug, setCompanySlug] = useState<string | null>(null);

  useEffect(() => {
    const loadCompany = async (): Promise<void> => {
      try {
        setLoading(true);
        setError(null);

        // Extract company slug from URL
        const slug = extractCompanySlug();

        // Fetch branding to get default company slug + display name
        const branding = await fetchDefaultCompany();
        const resolvedSlug = slug || branding?.slug || null;

        if (resolvedSlug) {
          setCompanySlug(resolvedSlug);
          setCompany({
            slug: resolvedSlug,
            name: branding?.displayName || resolvedSlug,
            id: null,
            branding: {},
            settings: {}
          });
        } else if (isReservedRoute()) {
          // Admin route, can't determine company — proceed with generic name
          setCompanySlug(null);
          setCompany({ slug: '', name: 'Portal', id: null, branding: {}, settings: {} });
        } else {
          setError('Could not determine the BC company for this tenant. Please contact your administrator.');
        }

        setLoading(false);
      } catch (err) {
        console.error('[CompanyContext] Error loading company:', err);
        setError((err as Error).message || 'Failed to load company');
        setLoading(false);
      }
    };

    loadCompany();

    // Re-load when URL changes
    const handleLocationChange = (): void => {
      const newSlug = extractCompanySlug();
      if (newSlug !== companySlug) {
        loadCompany();
      }
    };

    window.addEventListener('popstate', handleLocationChange);
    return () => window.removeEventListener('popstate', handleLocationChange);
  }, [companySlug]);

  // Apply company branding when company loads
  useEffect(() => {
    if (company && company.branding) {
      const root = document.documentElement;

      // Apply CSS variables for dynamic branding
      if (company.branding.primaryColor) {
        root.style.setProperty('--company-primary-color', company.branding.primaryColor);
      }
      if (company.branding.secondaryColor) {
        root.style.setProperty('--company-secondary-color', company.branding.secondaryColor);
      }

      // Update page title
      setBaseTitle(company.name
        ? `${company.name} - Goodstuff Portal`
        : 'Goodstuff Portal');
    }
  }, [company]);

  /**
   * Build URL path with company prefix (URL-encoded)
   * @param {string} path - The page path (e.g., 'dashboard', 'login')
   * @returns {string} Full path with company
   */
  const buildPath = (path: string): string => {
    const slug = companySlug || 'Portal';
    const cleanPath = path.startsWith('/') ? path.slice(1) : path;
    return `/${encodeSlug(slug)}/${cleanPath}`;
  };

  /**
   * Navigate to a path within the current company
   * @param {string} path - The page path
   */
  const navigateTo = (path: string): void => {
    window.location.href = buildPath(path);
  };

  const value: CompanyContextValue = {
    company,
    companySlug,
    loading,
    error,
    isCompanyLoaded: !loading && !error && company !== null,
    buildPath,
    navigateTo,
    encodeSlug,
  };

  // Show loading state
  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        flexDirection: 'column',
        gap: '1rem'
      }}>
        <div className="loading-spinner"></div>
        <p>Loading company...</p>
      </div>
    );
  }

  // Show error state
  if (error && !company) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        flexDirection: 'column',
        gap: '1rem',
        padding: '2rem',
        textAlign: 'center'
      }}>
        <h2>Company Not Found</h2>
        <p style={{ color: '#d32f2f' }}>{error}</p>
        <p style={{ marginTop: '1rem', color: '#666' }}>
          Please check the URL and ensure you&apos;re using the correct company identifier.
        </p>
      </div>
    );
  }

  return (
    <CompanyContext.Provider value={value}>
      {children}
    </CompanyContext.Provider>
  );
};

export default CompanyContext;
